import Vue from "vue";
import type { AxiosError } from "axios";
import router from "@/router";
import store from "@/store";
import { Endpoints } from "@/enums/endpoints.enum";
import {
  redactHeaders,
  redactBody,
  buildCurl,
  extractRequestId,
} from "@/helpers/errorRedaction";

export interface ErrorReport {
  status: number | null;
  statusText: string | null;
  message: string | null;
  method: string;
  url: string;
  requestId: string | null;
  pageUrl: string;
  pageName: string | null;
  timestamp: string;
  userAgent: string;
  appMode: string;
  curl: string;
  requestHeaders: Record<string, unknown>;
  requestBody: unknown;
  responseHeaders: Record<string, unknown>;
  responseBody: unknown;
  signature: string;
  duplicateCount: number;
}

interface ErrorReportingState {
  visible: boolean;
  report: ErrorReport | null;
  lastReportedAt: number;
  sending: boolean;
  sendResult: "success" | "error" | null;
}

function extractBackendMessage(responseBody: unknown): string | null {
  if (!responseBody) return null;
  if (typeof responseBody === "string") return responseBody || null;
  if (typeof responseBody !== "object") return null;
  const body = responseBody as Record<string, any>;

  if (typeof body.message === "string") return body.message;
  if (body.message && typeof body.message === "object") {
    if (typeof body.message.message === "string") return body.message.message;
    if (typeof body.message.description === "string") return body.message.description;
    if (typeof body.message.code === "string") return body.message.code;
  }
  if (typeof body.error === "string") return body.error;
  if (body.errors) {
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      const first = body.errors[0];
      if (typeof first === "string") return first;
      if (first && typeof first === "object") {
        if (typeof first.message === "string") return first.message;
        if (typeof first.detail === "string") return first.detail;
      }
    } else if (typeof body.errors === "object") {
      const keys = Object.keys(body.errors);
      if (keys.length > 0) {
        const first = (body.errors as Record<string, unknown>)[keys[0]];
        if (typeof first === "string") return first;
        if (Array.isArray(first) && typeof first[0] === "string") return first[0];
      }
    }
  }
  return null;
}

function stringifyForDisplay(value: unknown, limit = 5000): string {
  if (value === null || value === undefined) return "";
  let str: string;
  if (typeof value === "string") {
    str = value;
  } else {
    try {
      str = JSON.stringify(value, null, 2);
    } catch {
      str = String(value);
    }
  }
  if (str.length > limit) return str.slice(0, limit) + "\n…(truncated)";
  return str;
}

const truncateString = (value: unknown, max: number): unknown => {
  if (typeof value !== "string" || value.length <= max) return value;
  return value.slice(0, max - 3) + "...";
};

const SENT_STORAGE_KEY = "khabeer_err_sent";
const SENT_TTL_MS = 30 * 60 * 1000;

function hasRecentlySent(signature: string): boolean {
  try {
    const raw = sessionStorage.getItem(SENT_STORAGE_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw) as Record<string, number>;
    const ts = map[signature];
    if (!ts) return false;
    return Date.now() - ts <= SENT_TTL_MS;
  } catch {
    return false;
  }
}

function markSent(signature: string): void {
  try {
    const raw = sessionStorage.getItem(SENT_STORAGE_KEY);
    const map: Record<string, number> = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const now = Date.now();
    for (const key of Object.keys(map)) {
      if (now - map[key] > SENT_TTL_MS) delete map[key];
    }
    map[signature] = now;
    sessionStorage.setItem(SENT_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable or quota exceeded — acceptable silent failure
  }
}

class ErrorReportingService {
  state: ErrorReportingState = Vue.observable<ErrorReportingState>({
    visible: false,
    report: null,
    lastReportedAt: 0,
    sending: false,
    sendResult: null,
  });

  reportError(error: AxiosError): void {
    const config = error.config || ({} as AxiosError["config"]);
    const response = error.response;

    const method = (config?.method || "GET").toUpperCase();
    const baseURL = config?.baseURL || "";
    const rawUrl = config?.url || "";
    const fullUrl = /^https?:\/\//i.test(rawUrl)
      ? rawUrl
      : `${baseURL.replace(/\/$/, "")}/${rawUrl.replace(/^\//, "")}`;

    const status = response?.status ?? null;
    const statusText = response?.statusText ?? null;
    const responseHeaders = (response?.headers || {}) as Record<string, unknown>;
    const responseBody = response?.data ?? null;

    const requestHeaders = redactHeaders(
      (config?.headers as Record<string, unknown> | undefined) || {}
    ) as Record<string, unknown>;
    const requestBody = redactBody(config?.data);

    const currentRoute = router.currentRoute;
    const pageName =
      (currentRoute && (currentRoute.name as string | null | undefined)) || null;

    const newReport: ErrorReport = {
      status,
      statusText,
      message: extractBackendMessage(responseBody),
      method,
      url: fullUrl,
      requestId: extractRequestId(responseHeaders),
      pageUrl: window.location.href,
      pageName,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      appMode: String(import.meta.env.VITE_APP_MODE || "unknown"),
      curl: buildCurl(config),
      requestHeaders,
      requestBody,
      responseHeaders,
      responseBody,
      signature: `${method}:${fullUrl}:${status ?? "network"}`,
      duplicateCount: 0,
    };

    if (
      this.state.visible &&
      this.state.report &&
      this.state.report.signature === newReport.signature
    ) {
      this.state.report.duplicateCount += 1;
      this.state.lastReportedAt = Date.now();
      return;
    }

    this.state.report = newReport;
    this.state.visible = true;
    this.state.lastReportedAt = Date.now();
    this.state.sending = false;
    this.state.sendResult = null;
  }

  dismiss(): void {
    this.state.visible = false;
    this.state.report = null;
    this.state.sendResult = null;
    this.state.sending = false;
  }

  wasRecentlyReported(): boolean {
    return Date.now() - this.state.lastReportedAt < 1000;
  }

  formatAsText(report: ErrorReport): string {
    const user = store.state.user;
    const officerLine = user
      ? `${user.name || ""} <${user.email || ""}> (#${user.id || ""}, uuid=${user.uuid || ""})`
      : "(unauthenticated)";

    const lines: string[] = [];
    lines.push("=== Khabeer error report ===");
    lines.push(`Status       : ${report.status ?? "Network error / no response"}${report.statusText ? " " + report.statusText : ""}`);
    lines.push(`Message      : ${report.message ?? "(none)"}`);
    lines.push(`Method & URL : ${report.method} ${report.url}`);
    lines.push(`Request ID   : ${report.requestId ?? "(n/a)"}`);
    lines.push(`Page         : ${report.pageName ?? "(unknown)"} — ${report.pageUrl}`);
    lines.push(`Timestamp    : ${report.timestamp}`);
    lines.push(`Officer      : ${officerLine}`);
    lines.push(`App mode     : ${report.appMode}`);
    lines.push(`User agent   : ${report.userAgent}`);
    if (report.duplicateCount > 0) {
      lines.push(`Duplicates   : +${report.duplicateCount} similar errors rolled up`);
    }
    lines.push("");
    lines.push("--- Request headers (redacted) ---");
    lines.push(stringifyForDisplay(report.requestHeaders, 3000));
    lines.push("");
    lines.push("--- Request body (redacted) ---");
    lines.push(stringifyForDisplay(report.requestBody, 5000));
    lines.push("");
    lines.push("--- Response body ---");
    lines.push(stringifyForDisplay(report.responseBody, 5000));
    lines.push("");
    lines.push("--- cURL (redacted) ---");
    lines.push(report.curl);
    return lines.join("\n");
  }

  async sendToSupport(): Promise<void> {
    const report = this.state.report;
    if (!report || this.state.sending || this.state.sendResult === "success") return;

    const { default: toastService } = await import("@/services/toast.service");

    if (hasRecentlySent(report.signature)) {
      this.state.sendResult = "success";
      toastService.ShowSuccess("error-modal-already-reported");
      return;
    }

    const { default: axios } = await import("@/helpers/axios.config");

    this.state.sending = true;
    try {
      await axios.post(
        Endpoints.KhabeerErrorReports,
        {
          status: report.status,
          message: truncateString(report.message, 5000),
          method: truncateString(report.method, 10),
          url: truncateString(report.url, 2000),
          request_id: truncateString(report.requestId, 200),
          page_url: truncateString(report.pageUrl, 2000),
          page_name: truncateString(report.pageName, 200),
          timestamp: truncateString(report.timestamp, 40),
          user_agent: truncateString(report.userAgent, 500),
          app_mode: truncateString(report.appMode, 40),
          curl: truncateString(report.curl, 20000),
          request_headers: report.requestHeaders,
          request_body: report.requestBody,
          response_headers: report.responseHeaders,
          response_body: report.responseBody,
          duplicate_count: report.duplicateCount,
        },
        { skipErrorModal: true } as any
      );
      this.state.sendResult = "success";
      markSent(report.signature);
      toastService.ShowSuccess("error-modal-sent");
    } catch (error) {
      this.state.sendResult = "error";
      toastService.ShowError("error-modal-send-failed");
    } finally {
      this.state.sending = false;
    }
  }
}

export default new ErrorReportingService();
